import { createServerFn } from "@tanstack/react-start";

import type { CoachSelect } from "~/../drizzle/schema";
import { authMiddleware } from "~/lib/auth-middleware";

/**
 * Coach account approval status (tier 1).
 *
 * This is about whether the person is approved as a coach at all —
 * NOT about marketplace visibility (that's `MarketplaceStatus`).
 */
export type CoachAccountStatus = "pending" | "approved" | null;

type CoachStatusResponse = {
  isCoach: boolean;
  coachId: string | null;
  /**
   * Whether this coach has a provisioned GoHighLevel workspace. Today's menu
   * links "Marketing Suite" on this; the redesign's menu reads
   * `CoachAccount.hasSuite`, which asks whether she holds the Suite.
   */
  hasMarketingSuiteAccess: boolean;
  /** Tier 1: is this person approved as a coach? */
  accountStatus: CoachAccountStatus;
  /**
   * Tier 1 was explicitly revoked (`tier1RevokedAt` set) — distinguishes a
   * revoked coach from one who was simply never approved. A revoked coach
   * must never render as "under review."
   */
  isRevoked: boolean;
  /** Tier 2: is this coach visible on the marketplace? */
  isMarketplaceApproved: boolean;
  stripeChargesEnabled: boolean;
  contractSigned: boolean;
};

/** Derive the tier 1 (coach account) approval — pending vs approved. */
export function deriveAccountStatus(
  coach: Pick<CoachSelect, "approvedAt"> | null,
): CoachAccountStatus {
  if (!coach) return null;
  if (coach.approvedAt) return "approved";
  return "pending";
}

/** Check if the currently authenticated Clerk user is also a coach. */
export const getCoachStatus = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<CoachStatusResponse> => {
    const { clerkUserId, email, id: dbUserId } = context.user;
    if (!email) {
      return {
        isCoach: false,
        coachId: null,
        hasMarketingSuiteAccess: false,
        accountStatus: null,
        isRevoked: false,
        isMarketplaceApproved: false,
        stripeChargesEnabled: false,
        contractSigned: false,
      };
    }

    const { findAndLinkCoachForClerkUser } =
      await import("~/lib/server/coach-clerk-link");
    const coach = await findAndLinkCoachForClerkUser(
      clerkUserId,
      email,
      dbUserId,
    );

    if (!coach) {
      return {
        isCoach: false,
        coachId: null,
        hasMarketingSuiteAccess: false,
        accountStatus: null,
        isRevoked: false,
        isMarketplaceApproved: false,
        stripeChargesEnabled: false,
        contractSigned: false,
      };
    }

    return {
      isCoach: true,
      coachId: coach.id,
      hasMarketingSuiteAccess: coach.ghlLocationId !== null,
      accountStatus: deriveAccountStatus(coach),
      isRevoked: !!coach.tier1RevokedAt,
      isMarketplaceApproved: !!coach.marketplaceApprovedAt,
      stripeChargesEnabled: !!coach.stripeChargesEnabled,
      contractSigned: !!coach.contractSigned,
    };
  });
